/**
 * Chat Agents List Page.
 * Displays a card grid of all chat agents for the current organization.
 * Matches the design: card grid with agent name, description, icon, timestamps.
 */

import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/modal';
import { ROUTES } from '@/constants/routes';
import { useOrgStore } from '@/stores/org-store';
import { useChatAgentsStore } from '@/stores/chat-agents-store';
import { listChatAgents, deleteChatAgent } from '@/services/chat-agents/agents';
import { AgentCard } from './components/agent-card';
import type { ChatAgent } from '@/types/chat-agent-types';

export function ChatAgentsListPage() {
  const navigate = useOrgNavigate();
  const currentOrg = useOrgStore((s) => s.currentOrg);
  const { agents, setAgents, isLoadingAgents, setLoadingAgents } =
    useChatAgentsStore();
  const [error, setError] = useState<string | null>(null);
  const [agentToDelete, setAgentToDelete] = useState<ChatAgent | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchAgents = async () => {
      if (!currentOrg?.orgid) return;
      setLoadingAgents(true);
      setError(null);
      try {
        const result = await listChatAgents(currentOrg.orgid);
        setAgents(result);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load agents');
      } finally {
        setLoadingAgents(false);
      }
    };
    fetchAgents();
  }, [currentOrg?.orgid, setAgents, setLoadingAgents]);

  const handleDeleteConfirm = async () => {
    if (!currentOrg?.orgid || !agentToDelete) return;
    setIsDeleting(true);
    try {
      await deleteChatAgent(currentOrg.orgid, agentToDelete.id);
      setAgents(agents.filter((a) => a.id !== agentToDelete.id));
      setAgentToDelete(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete agent');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className='w-full'>
      {/* Header */}
      <div className='flex items-center justify-between mb-6'>
        <div>
          <h1 className='text-2xl font-bold tracking-tight'>Chat Agents</h1>
          <p className='text-sm text-muted-foreground mt-1'>
            Create agentic chat applications that work with your data
          </p>
        </div>
        <Button onClick={() => navigate(ROUTES.CHAT_AGENTS.NEW)}>
          <Plus className='mr-2 h-4 w-4' />
          New Agent
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className='rounded-lg border border-destructive bg-destructive/10 p-4 mb-6'>
          <p className='text-sm text-destructive'>{error}</p>
        </div>
      )}

      {/* Loading */}
      {isLoadingAgents && (
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3'>
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className='h-40 rounded-lg border border-border bg-muted animate-pulse'
            />
          ))}
        </div>
      )}

      {/* Agent Grid */}
      {!isLoadingAgents && agents.length > 0 && (
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3'>
          {agents.map((agent) => (
            <AgentCard
              key={agent.id}
              agent={agent}
              onDelete={setAgentToDelete}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoadingAgents && agents.length === 0 && !error && (
        <div className='flex flex-col items-center justify-center py-16 text-center'>
          <div className='h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4'>
            <Plus className='h-8 w-8 text-muted-foreground' />
          </div>
          <h3 className='font-semibold text-lg'>No chat agents yet</h3>
          <p className='text-sm text-muted-foreground mt-1 mb-4'>
            Create your first chat agent to get started.
          </p>
          <Button onClick={() => navigate(ROUTES.CHAT_AGENTS.NEW)}>
            <Plus className='mr-2 h-4 w-4' />
            Create Agent
          </Button>
        </div>
      )}

      {/* Delete confirmation modal */}
      <Modal
        isDialogOpen={!!agentToDelete}
        setIsDialogOpen={(open) => {
          if (!open) setAgentToDelete(null);
        }}
        title='Delete Agent'
        description={`Are you sure you want to delete "${agentToDelete?.name}"? This action cannot be undone.`}
        footer={
          <div className='flex gap-3'>
            <Button
              variant='outline'
              onClick={() => setAgentToDelete(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        }
      />
    </div>
  );
}
